import { useState, useEffect, useRef } from 'react';
import { SxStyleProp, Box, Flex, Text, Close } from 'theme-ui';
import { useTranslator } from '@picsart/rc/services/localization';
import { useSelector, useDispatch } from 'react-redux';

import Icon from 'shared/Icon';
import { isSubscribed } from 'modules/user';
import { getUsedPaidFeatures } from 'modules/scenes';
import { getPremiumData, showCheckoutModal } from 'modules/checkoutModal';

const containerStyle: SxStyleProp = {
  position: 'absolute',
  borderRadius: '8px',
  bg: '#282b36cc',
  width: 510,
  p: 5,
  alignItems: 'center',
  boxShadow: 'default',
  bottom: 130,
  left: 470,
  zIndex: 'popup'
};
const textStyle: SxStyleProp = {
  mx: 20,
  fontSize: '12px',
  mb: 0,
  whiteSpace: 'break-spaces',
  color: '#e9e9e9',
  span: {
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 'bold'
  }
};
const titleStyle: SxStyleProp = {
  mx: 20,
  fontSize: 2,
  mb: 0,
  fontWeight: 'bold',
  whiteSpace: 'break-spaces',
  color: '#e9e9e9',
  span: {
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 'bold'
  }
};

type RemoveWatermarkPopupProps = {
  className?: string;
};

export const RemoveWatermarkPopup = ({ className }: RemoveWatermarkPopupProps) => {
  const dispatch = useDispatch();
  const [isVisible, setVisible] = useState(false);
  const isUserSubscribed = useSelector(isSubscribed);
  const paidFeatures = useSelector(getUsedPaidFeatures);
  const premiumItems = useSelector(getPremiumData);
  const wasShown = useRef(false);
  const isFirstLoad = useRef(true);
  const translator = useTranslator();
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    setVisible(!wasShown.current && paidFeatures.length !== 0);
  }, [paidFeatures, wasShown]);

  if (isUserSubscribed) {
    return null;
  }

  const close = () => {
    setVisible(false);
    wasShown.current = true;
  };

  const clickHandler = () => {
    dispatch(showCheckoutModal('watermark_notification', premiumItems));
    close();
  };

  return (
    isVisible && (
      <Flex sx={containerStyle} className={className}>
        <Box sx={{ width: 24 }}>
          <Icon name="crown-circle-gen" sx={{ path: { fill: '#fda513' } }} />
        </Box>
        <Box sx={{ flex: 12 }}>
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Text variant="body" sx={titleStyle}>
              {translator('Upgrade to Gold')}
            </Text>
            <Close onClick={close} />
          </Flex>
          <Text variant="body" sx={textStyle}>
            {translator("You're_using_a_premium_feature.")}{' '}
            <Text data-test="remove-watermark-popup_link" as="span" onClick={clickHandler}>
              {translator('Click_here')}
            </Text>{' '}
            {translator('to remove the watermark and enjoy the full experience.')}
          </Text>
        </Box>
      </Flex>
    )
  );
};
